// Core primitives shared by every Adobe event module — types, globals, and the data-layer push function

export type AdobeDataLayerEvent = {
  event: string;
  [key: string]: unknown;
};

type Satellite = {
  track: (eventName: string, payload?: Record<string, unknown>) => void;
  pageBottom?: () => void;
};

declare global {
  interface Window {
    adobeDataLayer?: AdobeDataLayerEvent[];
    _satellite?: Satellite;
  }
}

// Descriptive click-type — historically 'button' | 'link', now a freeform string like 'accordion-button' or 'header-link'
export type ClickType = string;

// Strips query string and hash from a URL, returning origin + pathname only; '' if it can't be parsed.
// Every data-layer URL property uses this — only `urlFull` is allowed to keep the query string.
export const getCleanUrl = (url: string): string => {
  if (!url) return '';
  try {
    const parsed = new URL(url, typeof window !== 'undefined' ? window.location.href : 'http://x');
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return '';
  }
};

// Returns window._satellite or null (SSR or before the Launch script has loaded)
export const getSatellite = (): Satellite | null => {
  if (typeof window === 'undefined') return null;
  return window._satellite ?? null;
};

// Top-level payload keys shared by every event; any other key holds the event-specific
// data (click/download/search/form/video) which must always render last in the payload
const RESERVED_EVENT_KEYS = new Set(['event', 'eventName', 'eventDesc', 'page']);

// Returns a copy of the payload with keys sorted alphabetically, but the event-specific
// property (anything outside the reserved keys) forced to the end
const orderEventPayload = (payload: AdobeDataLayerEvent): AdobeDataLayerEvent => {
  const keys = Object.keys(payload);
  const reserved = keys.filter((key) => RESERVED_EVENT_KEYS.has(key)).sort();
  const specific = keys.filter((key) => !RESERVED_EVENT_KEYS.has(key)).sort();
  const ordered = {} as AdobeDataLayerEvent;
  for (const key of [...reserved, ...specific]) ordered[key] = payload[key];
  return ordered;
};

// Pushes a typed event onto the Adobe Client Data Layer; safe before Launch loads (queue pattern)
export const pushAdobeEvent = (event: AdobeDataLayerEvent): void => {
  if (typeof window === 'undefined') return;
  window.adobeDataLayer = window.adobeDataLayer ?? [];
  const ordered = orderEventPayload(event);
  // TEMP debug: logs true key order as a string (DevTools object trees always show keys alphabetically)
  console.log('[adobe] key order:', Object.keys(ordered).join(' → '), JSON.stringify(ordered));
  window.adobeDataLayer.push(ordered);
};
